'use client';
import clsx from 'clsx';
import { useState, useEffect } from 'react';
import { Bell } from '../../../../components/Icons/Bell';
import { useDropdownContext } from '../../../../components/DropdownProvider';
import {
  CLOSE_NOTIFICATIONS_ACTION,
  OPEN_NOTIFICATIONS_ACTION,
} from '../../../../components/DropdownProvider/useSingleDropdown';
import { fetchNotifications } from '../../../../services/notifications';
import useSWRImmutable from 'swr/immutable';
import { UnreadBadge } from './UnreadBadge';
import { useSegmentInteraction } from '../../../../lib/analytics/useSegmentInteraction';
import { fetchNotificationsKey } from '../NotificationSidebar/constants';
import { type NotificationsState } from '../NotificationSidebar/types';

function countUnreadNotifications(state: NotificationsState) {
  return (state?.notifications?.items ?? []).filter(n => !n.isRead).length;
}

export function NotificationsBell() {
  const { state, dispatch } = useDropdownContext();

  // State to store previous unread count
  // If we dont do this, we will see the red badge dissapear and reappear after sidebar closes
  const [prevUnreadCount, setPrevUnreadCount] = useState<number>(0);

  const { data: notificationsState, isValidating } = useSWRImmutable<NotificationsState>(
    fetchNotificationsKey,
    fetchNotifications,
  );

  useEffect(() => {
    if (notificationsState) {
      setPrevUnreadCount(countUnreadNotifications(notificationsState));
    }
  }, [notificationsState]);

  const fireBellClickedEvent = useSegmentInteraction({
    eventName: 'NC Bell Icon Clicked',
    attributes: {},
  });

  function handleButtonClick() {
    if (!state.isNotificationSidebarOpen) {
      window.scrollTo(0, 0);
      dispatch(OPEN_NOTIFICATIONS_ACTION);
    } else {
      dispatch(CLOSE_NOTIFICATIONS_ACTION);
    }

    fireBellClickedEvent();
  }

  const isSidebarOpen = state.isNotificationSidebarOpen ?? false;
  const notificationUnreadCount = isValidating
    ? prevUnreadCount
    : countUnreadNotifications(notificationsState);

  return (
    <button
      onClick={handleButtonClick}
      className={clsx(
        'relative',
        'flex',
        'items-center',
        'justify-center',
        'h-[49px]',
        'w-[49px]',
        'cursor-pointer',
        'hover:bg-neutral-lightest',
        'hover:text-green-dark',
        'rounded-[500px]',
        'active:bg-green-lightest',
        'active:text-green-dark',
        {
          'bg-green-lightest': isSidebarOpen,
          'text-green-dark': isSidebarOpen,
        },
      )}
    >
      <UnreadBadge unreadCount={notificationUnreadCount} />
      <Bell width={35} height={35} />
    </button>
  );
}
